import { PropsWithChildren, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { FONT_DATA, FONT_DISPLAY } from '../../theme/themes';

// Shared building blocks for the home page cards: one card shell plus the
// loading / empty / error bodies, so every section states the same way.

// Hero and content share this centered frame so they align on wide screens.
export const HOME_CONTENT_MAX_WIDTH = 1220;

export const eyebrowSx = {
  fontFamily: FONT_DATA,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.11em',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;

type HomeCardProps = PropsWithChildren<{
  title: string;
  eyebrow?: string;
  actions?: ReactNode;
}>;

export const HomeCard = ({
  title,
  eyebrow,
  actions,
  children,
}: HomeCardProps) => (
  <Paper
    elevation={1}
    sx={{
      borderRadius: '10px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    <Box
      sx={{ px: 2.5, pt: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}
    >
      <Typography
        component="h2"
        sx={{
          fontFamily: FONT_DISPLAY,
          fontWeight: 750,
          fontSize: 16.5,
          letterSpacing: '-0.01em',
          flex: 1,
          m: 0,
        }}
      >
        {title}
      </Typography>
      {eyebrow && (
        <Typography component="span" sx={eyebrowSx}>
          {eyebrow}
        </Typography>
      )}
    </Box>
    <Box sx={{ px: 2.5, pt: 1.5, pb: 2.25, flex: 1, minWidth: 0 }}>
      {children}
    </Box>
    {actions && (
      <Box sx={{ px: 1.75, pb: 1.5, display: 'flex', gap: 0.5 }}>{actions}</Box>
    )}
  </Paper>
);

export const KindBadge = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT_DATA,
      fontSize: 9.5,
      fontWeight: 600,
      letterSpacing: '0.09em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      bgcolor: 'background.default',
      borderRadius: '5px',
      px: 1,
      py: 0.5,
      minWidth: 46,
      textAlign: 'center',
      flex: 'none',
    }}
  >
    {label}
  </Box>
);

export const HomeSkeletonRows = ({ rows = 3 }: { rows?: number }) => (
  <Box>
    {Array.from({ length: rows }).map((_, i) => (
      <Box
        key={i}
        data-testid="home-skeleton"
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25 }}
      >
        <Skeleton variant="rounded" width={46} height={20} />
        <Skeleton variant="text" sx={{ flex: 1 }} />
        <Skeleton variant="text" width={52} />
      </Box>
    ))}
  </Box>
);

type HomeEmptyStateProps = PropsWithChildren<{
  icon?: ReactNode;
  title: string;
  action?: ReactNode;
}>;

export const HomeEmptyState = ({
  icon,
  title,
  action,
  children,
}: HomeEmptyStateProps) => (
  <Box
    sx={{
      bgcolor: 'background.default',
      borderRadius: '9px',
      px: 2.25,
      py: 2.75,
      textAlign: 'center',
      color: 'text.secondary',
    }}
  >
    {icon}
    <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 13.5 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: 13 }}>{children}</Typography>
    {action && <Box sx={{ mt: 1.25 }}>{action}</Box>}
  </Box>
);

type HomeErrorStateProps = {
  title: string;
  description: string;
  onRetry?: () => void;
};

export const HomeErrorState = ({
  title,
  description,
  onRetry,
}: HomeErrorStateProps) => (
  <Box
    sx={theme => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      bgcolor: alpha(theme.palette.error.main, 0.08),
      borderRadius: '9px',
      px: 2,
      py: 1.75,
    })}
  >
    <ErrorOutlineRoundedIcon
      sx={{ fontSize: 18, color: 'error.main', mt: 0.25 }}
    />
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>{title}</Typography>
      <Typography sx={{ color: 'text.secondary', fontSize: 12.5 }}>
        {description}
      </Typography>
    </Box>
    {onRetry && (
      <Button size="small" onClick={onRetry}>
        Retry
      </Button>
    )}
  </Box>
);
